import { Icon, type IconName } from "@/lib/icons/Icon";
import styles from "./FullPageMessage.module.css";

/**
 * **FullPageMessage** — wzorcowy komponent pełnoekranowego, blokującego
 * komunikatu dla visitora.
 *
 * **Kiedy używać:** strona nie może być wyrenderowana w normalnym stanie
 * (maintenance, błąd konfiguracji blokujący homepage, krytyczna usterka),
 * a visitor NIE powinien widzieć nagłówka, stopki ani innej zawartości CMS.
 * Komponent zajmuje cały viewport (`min-height: 100vh`) i ZASTĘPUJE
 * `PublicLayoutView` — używaj go jako **early-return** z route handler'a
 * lub PublicLayout, BEZ wrappingu w PublicLayout.
 *
 * **Architektura — kiedy NIE PublicLayout:**
 *
 * Normalne strony idą `PublicLayout > HDefault + main + FDefault`. Chrome
 * (header/footer) renderuje okna z bazy + branding domeny. Dla pełnoekranowego
 * komunikatu chrome przeszkadza — visitor zobaczy nagłówek (mylące:
 * "strona jakoś działa") + stopkę (z linkami do innych podstron które też
 * mogą być rozwalone). Dlatego komunikat blokujący renderuje się STANDALONE.
 *
 * **Wzorzec early-return** (Server Component):
 *
 * ```tsx
 * // app/[locale]/page.tsx
 * export default async function Home({ params }) {
 *   const { locale } = await params;
 *   const cmsConfig = await fetchCmsConfig({ lang: locale });
 *
 *   if (someBlockingCondition(cmsConfig)) {
 *     return (
 *       <FullPageMessage
 *         title={t.someError.title}
 *         body={t.someError.body}
 *         footnote={t.someError.footnote}
 *         variant="warning"
 *       />
 *     );
 *   }
 *
 *   return <PublicLayout>...</PublicLayout>;
 * }
 * ```
 *
 * **Zawsze lokalizuj:** stringi pobieraj z `getMessages(locale)` —
 * `app/[locale]/...` routes mają locale z params; admin contexty mogą
 * używać hardcoded PL (admin panel jest mono-language).
 *
 * **Auto-refresh:** dla stanów "tymczasowo nie działa" (maintenance, await
 * for admin fix) podaj `autoRefreshSeconds` — komponent doda `<meta refresh>`
 * z tym interwałem. Visitor zobaczy poprawioną stronę bez ręcznego F5
 * po fix'ie. Bez `autoRefreshSeconds` brak auto-refresh — używaj dla
 * stanów "permanentnie zepsute" gdzie auto-refresh nic nie da.
 *
 * **Warianty wizualne (`variant` prop):**
 *   - `info` (default) — primary brand color (niebieski Imperial / fiolet
 *     premium / inne per szablon). Dla informacyjnych "w trakcie konfiguracji".
 *   - `warning` — orange/żółty. Dla problemów konfiguracyjnych admin musi
 *     naprawić ale strona nie jest "down" (np. invalid redirect URL).
 *   - `danger` — czerwony. Dla krytycznych błędów (database down, security
 *     breach detected) — rzadko używane.
 *
 * **NIE używaj dla:**
 *   - Inline błędów w komponentach (np. "Nie udało się załadować widgeta")
 *     — to nie blokuje strony, użyj lokalnego error UI w komponencie.
 *   - Admin-only komunikatów — admin chrome (AdminTopBar) zwykle powinien
 *     zostać widoczny żeby admin mógł zareagować. Patrz pattern
 *     `InvalidTemplateAdminBlock` (renderowany WEWNĄTRZ AdminProvider).
 *
 * **Anti-patterns:**
 *   - Renderowanie WEWNĄTRZ PublicLayout — chrome leak'uje
 *   - Hardcoded stringi w PL — visitor /en zobaczy mieszankę
 *   - `position: fixed` zamiast `min-height: 100vh` — break'uje scroll
 *     na bardzo długiej treści (rzadko, ale unikamy)
 *
 * @example Maintenance — auto-refresh + info variant
 * ```tsx
 * <FullPageMessage
 *   title={t.maintenance.title}
 *   subtitle={t.maintenance.subtitle}
 *   body={t.maintenance.body}
 *   footnote={t.maintenance.footnote}
 *   icon="warning"
 *   autoRefreshSeconds={30}
 * />
 * ```
 *
 * @example Config error — bez auto-refresh + warning variant
 * ```tsx
 * <FullPageMessage
 *   title={t.homepage.redirectLoopTitle}
 *   body={t.homepage.redirectLoopMessage}
 *   footnote={t.homepage.redirectLoopHint}
 *   icon="warning"
 *   variant="warning"
 * />
 * ```
 */
export function FullPageMessage({
  title,
  subtitle,
  body,
  footnote,
  action,
  icon = "warning",
  variant = "info",
  autoRefreshSeconds,
}: FullPageMessageProps) {
  return (
    <>
      {/* Auto-refresh meta tag — gdy autoRefreshSeconds podany, browser
          przeładuje stronę po N sekundach. Klasyczny 503 pattern. */}
      {autoRefreshSeconds !== undefined && (
        <meta httpEquiv="refresh" content={String(autoRefreshSeconds)} />
      )}
      <div className={styles.root} data-variant={variant}>
        <div className={styles.backdrop} aria-hidden="true" />
        <div className={styles.card}>
          <div className={styles.iconWrap}>
            {/* Pulse ring renderowany TYLKO gdy auto-refresh aktywny —
                visual cue że strona w stanie "transient", coś się zmieni. */}
            {autoRefreshSeconds !== undefined && (
              <span className={styles.pulse} aria-hidden="true" />
            )}
            <div className={styles.icon}>
              <Icon name={icon} />
            </div>
          </div>
          <h1 className={styles.title}>{title}</h1>
          {subtitle !== undefined && (
            <p className={styles.subtitle}>{subtitle}</p>
          )}
          {body !== undefined && <p className={styles.body}>{body}</p>}
          {action !== undefined && (
            <div className={styles.action}>{action}</div>
          )}
          {footnote !== undefined && (
            <>
              <div className={styles.divider} aria-hidden="true" />
              <p className={styles.footnote}>{footnote}</p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export type FullPageMessageProps = {
  /** Główny nagłówek (h1) — największy tekst, primary visual anchor. */
  title: string;
  /** Opcjonalny podtytuł między tytułem a body. Używa accent color wariantu. */
  subtitle?: string;
  /** Body paragraf — wyjaśnienie pod podtytułem. */
  body?: string;
  /** Footnote pod separatorem — call to action, contact info. */
  footnote?: string;
  /** Opcjonalny CTA slot — element (typowo `<a>`/`<Link>`/`<button>`) wstawiany
   *  między body a footnote'em. Używaj dla "Back to home" na 404, "Try again"
   *  na error itp. Caller dostarcza pełny element wraz ze stylami. */
  action?: React.ReactNode;
  /** Phosphor icon name dla circular badge. Default: `warning`. */
  icon?: IconName;
  /** Wariant wizualny — wpływa na kolor ikony, podtytułu, gradient backdrop'u. */
  variant?: "info" | "warning" | "danger";
  /** Auto-refresh meta tag interval (sekundy). Pominięty -> no auto-refresh.
   *  Gdy podany, dodatkowo renderuje pulse animation wokół ikony. */
  autoRefreshSeconds?: number;
};
